import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { shownMatchCategoryKey, shownMatchScore } from '@entities/search-run';
import {
  geoStepKey,
  tieBreakKey,
  toMarketComparison,
  type SelectionSlot,
  type SelectionState,
} from '@entities/selection';
import type { VehicleCardData } from '@entities/vehicle/types';
import { MarketPosition } from '@entities/vehicle/MarketPosition';
import { VehicleCard } from '@entities/vehicle/VehicleCard';
import { ROUTES } from '@shared/config/routes';
import { Badge, ScoreRing } from '@shared/ui';

import { PostCheckScoreBlock } from './PostCheckScore';
import { SelectionActions } from './SelectionActions';
import { SelectionBasis } from './SelectionBasis';
import styles from './SelectionCard.module.css';

interface SelectionCardProps {
  slot: SelectionSlot;
  /** Заказ: карточка ведёт на страницу автомобиля **внутри него**. */
  searchId: string;
  index: number;
  /** Состав заказа: из него берутся стадия, согласия и доступность действий. */
  selection: SelectionState;
  /** Karl рекомендует этот вариант. До Epic 25 всегда `false`. */
  recommended?: boolean | undefined;
}

/**
 * Карточка автомобиля четвёрки (FE-220, референс `screen4`).
 *
 * Собрана **из слотов `VehicleCard`**, а не поверх неё: карточка внутри
 * карточки — дефект вёрстки (DESIGN_SYSTEM §5.2), и он же приводит к двум
 * ценам и двум упоминаниям автохауса на одном экране.
 *
 * Четыре карточки **равны по весу**: рекомендованный вариант помечен бейджем и
 * стоит первым, но визуально не крупнее остальных (DESIGN_SYSTEM §7). Иначе
 * вёрстка начинает подсказывать выбор, который должен делать пользователь.
 *
 * **Все отметки — в строке над карточкой, а не поверх фотографии.** Бейдж на
 * снимке закрывает часть автомобиля, то есть данные, ради которых снимок и
 * показан. «Лучший выбор» получает ровно один вариант из четырёх — первый по
 * рангу отбора.
 *
 * Действия зависят от того, заморожен ли состав (§34.4–34.5): до запуска
 * углублённой проверки — «Не подходит» с заменой, после — «Взять в торг»,
 * «Снять с торга» и «Отказаться от этого автомобиля» без замены. Решает это
 * `SelectionActions`, а не карточка.
 *
 * Плашки «Geprüft» здесь нет и не будет — она означала бы, что CarViQ.DE
 * автомобиль проверил. Karl **спрашивает** у автохауса и приводит ответ
 * цитатой; обещать выполненную проверку нельзя (MVP_PRD §2).
 */
export function SelectionCard({
  slot,
  searchId,
  index,
  selection,
  recommended = false,
}: SelectionCardProps) {
  const { t } = useTranslation();

  const { vehicle, basis } = slot;
  const match = vehicle.match;
  const market = toMarketComparison(vehicle.market, vehicle.price.currency);
  const context = [
    basis.geo_step ? t(geoStepKey(basis.geo_step)) : null,
    basis.tie_break_reason ? t(tieBreakKey(basis.tie_break_reason)) : null,
  ].filter(Boolean);

  const card: VehicleCardData = {
    access: 'full',
    id: vehicle.vehicle_id,
    slug: vehicle.slug,
    title: vehicle.title,
    make: vehicle.make,
    model: vehicle.model,
    variant: vehicle.variant,
    body_type: vehicle.body_type as VehicleCardData['body_type'],
    price: vehicle.price,
    first_registration_date: vehicle.first_registration_date,
    mileage_km: vehicle.mileage_km,
    fuel_type: vehicle.fuel_type,
    transmission: vehicle.transmission,
    drivetrain: vehicle.drivetrain,
    power_kw: vehicle.power_kw,
    location: vehicle.location,
    cover_image: vehicle.cover_image,
    data_observed_at: slot.entered_at,
    platform_published_at: null,
    is_new: false,
    match: null,
    market,
    attention_count: vehicle.attention_count,
  };

  return (
    <div className={styles.wrap} data-slot={slot.slot}>
      {/* Строка состояния стоит **над** карточкой, а не поверх фотографии:
          бейдж на снимке закрывает часть автомобиля — то есть данные, ради
          которых снимок и показан (DESIGN_SYSTEM §5.5). */}
      <p className={styles.caption}>
        <span className={styles.position}>
          <span className="visually-hidden">{t('selection.card.position')}</span>
          <span className="tabular">{index + 1}</span>
        </span>
        {/* «Лучший выбор» — ровно один вариант из четырёх: первый по рангу
            отбора. Это факт ранжирования backend'а, а не вердикт о качестве
            автомобиля (MVP_PRD §38.1). */}
        {index === 0 ? <Badge tone="neutral">{t('catalog.topPick')}</Badge> : null}
        {recommended ? (
          <Badge tone="action" icon={<CheckCircleIcon weight="fill" />}>
            {t('selection.card.recommended')}
          </Badge>
        ) : null}
        {/* Автомобиль в работе: Karl ведёт по нему переговоры. Цвет не
            единственный носитель смысла — есть текст и иконка. */}
        {slot.status === 'selected' ? (
          <Badge tone="action" icon={<CheckCircleIcon weight="fill" />}>
            {t('selection.card.selected')}
          </Badge>
        ) : null}
        {/* Объявление снято — факт каталога, а не оценка: Karl подбирает
            замену, и об этом сказано прямо (§34.13). */}
        {vehicle.is_published ? null : (
          <Badge tone="attention" icon={<WarningIcon weight="fill" />}>
            {t('selection.card.unavailable')}
          </Badge>
        )}
      </p>

      <VehicleCard
        vehicle={card}
        index={index}
        // Внутри заказа карточка ведёт на **персональную** страницу автомобиля,
        // а не на публичную: там разбор соответствия этому запросу (§18).
        to={ROUTES.searchVehicle(searchId, vehicle.vehicle_id)}
        score={
          match === null ? (
            <p className={styles.noMatch}>{t('selection.card.noMatch')}</p>
          ) : (
            <div className={styles.score}>
              <ScoreRing
                score={shownMatchScore(match.score)}
                size={56}
                title={t('results.card.matchTitle')}
                label={t(shownMatchCategoryKey(match.score))}
              />
              <span className={styles.coverage}>
                {t('selection.card.coverage')}:{' '}
                <span className="tabular">
                  {t('selection.compare.unit.percent', {
                    value: Math.round(match.scoring_coverage),
                  })}
                </span>
              </span>
            </div>
          )
        }
        footnote={
          <div className={styles.footnote}>
            {/* Цена против медианы — виджетом, а не капсулой (FE-595): «−6 %
                zum Median» в одну строку не говорил, выше машина рынка или ниже
                и от какой суммы. */}
            <MarketPosition market={market} />

            {/* `attention_count = 0` **не** показывается как «замечаний нет»:
                такого вывода система делать не вправе (MVP_PRD §15). */}
            {vehicle.attention_count !== null && vehicle.attention_count > 0 ? (
              <div className={styles.signals}>
                <span className={`${styles.signal} ${styles.signalAttention}`}>
                  <WarningIcon size={14} weight="fill" aria-hidden="true" />
                  {t('vehicle.attention.count', { count: vehicle.attention_count })}
                </span>
              </div>
            ) : null}

            {vehicle.seller_name === null ? null : (
              <p className={styles.seller}>{vehicle.seller_name}</p>
            )}

            {context.length > 0 ? <p className={styles.context}>{context.join(' · ')}</p> : null}

            {/* Две оценки рядом и никогда не сложенные (§38.5). Появляются
                вместе с ответами углублённой проверки; до неё `post_check`
                пуст, и блок показывает «Нет данных», а не ноль. */}
            {slot.post_check === null ? null : (
              <PostCheckScoreBlock
                matchScore={match?.score ?? null}
                postCheck={slot.post_check}
                layout="full"
              />
            )}

            <SelectionBasis basis={basis} currency={vehicle.price.currency} />
          </div>
        }
        action={<SelectionActions searchId={searchId} slot={slot} selection={selection} />}
      />
    </div>
  );
}
